import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plane, ShieldCheck, MessageCircleHeart, FileCheck2, Menu, X,
  BadgePoundSterling, Clock, HandCoins, PhoneCall, Mail, Send,
  Sparkles, CheckCircle2, ArrowRight, Calculator,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { useLang, CONTACT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO = "https://images.unsplash.com/photo-1710028267880-f34d75a5ead6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const SUPPORT = "https://images.unsplash.com/photo-1702669010463-3f2088abc0e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const TRAVELER = "https://images.unsplash.com/photo-1627923146572-5e3d07f32980?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

function Header() {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const links = [
    { id: "how", label: t.nav.how },
    { id: "why", label: t.nav.why },
    { id: "check", label: t.nav.check },
    { id: "faq", label: t.nav.faq },
    { id: "contact", label: t.nav.contact },
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200/60">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="#" className="flex items-center gap-2" data-testid="brand-logo">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-sky-700 text-white">
            <Plane className="h-5 w-5 -rotate-45" />
          </span>
          <span className="font-head font-extrabold text-xl tracking-tight text-stone-900">Zbor<span className="text-sky-700">Help</span></span>
        </a>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button key={l.id} onClick={() => scrollTo(l.id)} data-testid={`nav-${l.id}`}
              className="text-sm font-medium text-stone-600 hover:text-sky-700 transition-colors">{l.label}</button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button onClick={toggle} data-testid="lang-toggle"
            className="text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white transition-colors">
            {lang === "ro" ? "EN" : "RO"}
          </button>
          <Button onClick={() => scrollTo("check")} data-testid="header-cta"
            className="hidden sm:inline-flex bg-amber-500 hover:bg-amber-600 text-stone-900 font-semibold rounded-full">
            {t.nav.check}
          </Button>
          <button className="lg:hidden text-stone-800" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-stone-50 border-t border-stone-200 px-6 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <button key={l.id} onClick={() => { scrollTo(l.id); setOpen(false); }}
              className="text-left py-1.5 text-stone-700 font-medium">{l.label}</button>
          ))}
        </div>
      )}
    </header>
  );
}

function Hero() {
  const { t } = useLang();
  const stats = [
    { v: t.hero.stat1, l: t.hero.stat1l },
    { v: t.hero.stat2, l: t.hero.stat2l },
    { v: t.hero.stat3, l: t.hero.stat3l },
  ];
  return (
    <section className="relative min-h-[92vh] flex items-center pt-16">
      <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
        <div className="max-w-2xl fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
            <Sparkles className="h-3.5 w-3.5 text-amber-400" /> {t.hero.badge}
          </span>
          <h1 className="mt-6 font-head font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white">
            {t.hero.title}
          </h1>
          <p className="mt-6 text-lg sm:text-xl leading-relaxed text-stone-200 max-w-xl">{t.hero.subtitle}</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Button onClick={() => scrollTo("check")} data-testid="hero-cta-button"
              className="h-14 px-8 text-base bg-amber-500 hover:bg-amber-600 text-stone-900 font-bold rounded-full shadow-lg shadow-amber-500/20">
              {t.hero.cta} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button onClick={() => scrollTo("contact")} data-testid="hero-cta-secondary" variant="outline"
              className="h-14 px-8 text-base rounded-full border-white/40 bg-white/5 text-white hover:bg-white hover:text-stone-900">
              {t.hero.cta2}
            </Button>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {stats.map((s, i) => (
              <div key={i} className="border-l border-white/25 pl-4">
                <div className="font-head font-extrabold text-2xl sm:text-3xl text-white">{s.v}</div>
                <div className="text-xs text-stone-300 mt-1 leading-tight">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Trust() {
  const { t } = useLang();
  const icons = [HandCoins, MessageCircleHeart, FileCheck2];
  return (
    <section className="bg-stone-50 py-12 lg:py-16">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-stone-900 max-w-2xl">{t.trust.title}</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.trust.items.map((it, i) => {
            const Icon = icons[i];
            return (
              <div key={i} className="bg-white border border-stone-200 rounded-3xl p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`trust-card-${i}`}>
                <div className="grid place-items-center h-12 w-12 rounded-2xl bg-sky-50 text-sky-700"><Icon className="h-6 w-6" /></div>
                <h3 className="mt-5 font-head font-semibold text-xl text-stone-900">{it.t}</h3>
                <p className="mt-2 text-stone-600 leading-relaxed">{it.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function How() {
  const { t } = useLang();
  return (
    <section id="how" className="bg-white py-12 lg:py-16 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-700">{t.nav.how}</span>
        <h2 className="mt-3 font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-stone-900">{t.how.title}</h2>
        <p className="mt-3 text-lg text-stone-600">{t.how.subtitle}</p>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {t.how.steps.map((s, i) => (
            <div key={i} className="relative fade-up" style={{ animationDelay: `${i * 120}ms` }} data-testid={`how-step-${i}`}>
              <div className="font-head font-extrabold text-6xl text-stone-100">0{i + 1}</div>
              <div className="-mt-8 pl-1">
                <h3 className="font-head font-semibold text-xl text-stone-900">{s.t}</h3>
                <p className="mt-2 text-stone-600 leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Why() {
  const { t } = useLang();
  return (
    <section id="why" className="bg-stone-900 py-12 lg:py-16 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="relative rounded-[2rem] overflow-hidden border border-white/10">
            <img src={TRAVELER} alt="" className="w-full h-[440px] object-cover" />
            <div className="absolute bottom-4 left-4 right-4 bg-stone-900/70 backdrop-blur border border-white/10 rounded-2xl p-4 flex items-center gap-3">
              <BadgePoundSterling className="h-8 w-8 text-amber-400 shrink-0" />
              <p className="text-sm text-stone-200">{t.hero.stat3l} · {t.hero.stat1l}</p>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <h2 className="font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">{t.why.title}</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {t.why.items.map((it, i) => (
              <div key={i} className="flex gap-4" data-testid={`why-item-${i}`}>
                <CheckCircle2 className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-head font-semibold text-lg text-white">{it.t}</h3>
                  <p className="mt-1 text-stone-400 leading-relaxed text-sm">{it.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const EST_AMOUNTS = { d1: 220, d2: 350, d3: 520 };

function Estimator({ band, setBand }) {
  const { t } = useLang();
  const gross = EST_AMOUNTS[band];
  const fee = Math.round(gross * 0.4);
  const net = gross - fee;
  const gbp = (n) => `£${n}`;

  return (
    <div className="mt-6 rounded-3xl border border-stone-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="estimator">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-11 w-11 rounded-2xl bg-amber-500 text-stone-900 shrink-0"><Calculator className="h-6 w-6" /></span>
        <div>
          <h3 className="font-head font-bold text-xl text-stone-900">{t.est.title}</h3>
          <p className="text-sm text-stone-500">{t.est.subtitle}</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div className="space-y-2">
          <Label className="text-sm font-medium text-stone-700">{t.est.distance}</Label>
          <Select value={band} onValueChange={setBand}>
            <SelectTrigger className="h-12" data-testid="estimator-distance"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="d1">{t.est.d1}</SelectItem>
              <SelectItem value="d2">{t.est.d2}</SelectItem>
              <SelectItem value="d3">{t.est.d3}</SelectItem>
            </SelectContent>
          </Select>
          <div className="pt-2 space-y-1.5 text-sm">
            <div className="flex justify-between text-stone-600">
              <span>{t.est.gross}</span><span className="font-semibold text-stone-900" data-testid="estimator-gross">{gbp(gross)}</span>
            </div>
            <div className="flex justify-between text-stone-600">
              <span>{t.est.fee}</span><span className="font-semibold text-stone-900" data-testid="estimator-fee">−{gbp(fee)}</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-sky-700 text-white p-6 text-center">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-sky-200">{t.est.net}</div>
          <div className="mt-2 font-head font-extrabold text-4xl sm:text-5xl" data-testid="estimator-net">{gbp(net)}</div>
        </div>
      </div>
      <p className="mt-5 text-xs text-stone-500 leading-relaxed">{t.est.note}</p>
    </div>
  );
}

function EligibilityForm() {
  const { t } = useLang();
  const empty = { full_name: "", email: "", phone: "", flight_number: "", flight_date: "", departure_airport: "", arrival_airport: "", airline: "", issue_type: "delay", delay_length: "", details: "" };
  const [form, setForm] = useState(empty);
  const [band, setBand] = useState("d2");
  const [consents, setConsents] = useState({ c1: false, c2: false, c3: false, c4: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleC = (k) => (v) => setConsents({ ...consents, [k]: !!v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) return toast.error(t.validation);
    if (!consents.c1 || !consents.c2 || !consents.c3 || !consents.c4) return toast.error(t.elig.consent_error);
    setLoading(true);
    const gross = EST_AMOUNTS[band];
    try {
      await axios.post(`${API}/submissions/eligibility`, {
        ...form,
        consent_accuracy: consents.c1,
        consent_contact: consents.c2,
        consent_no_guarantee: consents.c3,
        consent_terms: consents.c4,
        estimate_distance: t.est[band],
        estimate_gross: gross,
        estimate_net: gross - Math.round(gross * 0.3),
      });
      toast.success(t.elig.success);
      setForm(empty);
      setConsents({ c1: false, c2: false, c3: false, c4: false });
    } catch (err) {
      toast.error(t.error);
    } finally { setLoading(false); }
  };

  const consentRows = [
    { k: "c1", label: t.elig.consent1 },
    { k: "c2", label: t.elig.consent2 },
    { k: "c3", label: t.elig.consent3 },
  ];

  return (
    <section id="check" className="bg-stone-50 py-12 lg:py-16 scroll-mt-16">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-700">{t.nav.check}</span>
          <h2 className="mt-3 font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-stone-900">{t.elig.title}</h2>
          <p className="mt-3 text-lg text-stone-600">{t.elig.subtitle}</p>
        </div>

        <div className="mt-10 rounded-3xl border border-sky-200 bg-sky-50/70 p-6 sm:p-8" data-testid="fee-box">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-2xl bg-sky-700 text-white shrink-0"><BadgePoundSterling className="h-6 w-6" /></span>
            <h3 className="font-head font-bold text-xl text-stone-900">{t.elig.fee_title}</h3>
          </div>
          <ul className="mt-4 space-y-2">
            {[t.elig.fee1, t.elig.fee2, t.elig.fee3].map((f, i) => (
              <li key={i} className="flex gap-2 text-stone-700">
                <CheckCircle2 className="h-5 w-5 text-sky-700 shrink-0 mt-0.5" /><span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        <Estimator band={band} setBand={setBand} />

        <p className="mt-6 flex items-start gap-2 text-sm text-stone-600 bg-stone-100 border border-stone-200 rounded-2xl p-4" data-testid="elig-trust-note">
          <ShieldCheck className="h-5 w-5 text-sky-700 shrink-0 mt-0.5" />
          <span>{t.elig.trust_note}</span>
        </p>

        <form onSubmit={submit} className="mt-6 bg-white border border-stone-200 rounded-3xl p-6 sm:p-10 shadow-sm space-y-5" data-testid="eligibility-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t.elig.full_name} required><Input value={form.full_name} onChange={set("full_name")} data-testid="elig-full-name" className="h-12" /></Field>
            <Field label={t.elig.email} required><Input type="email" value={form.email} onChange={set("email")} data-testid="elig-email" className="h-12" /></Field>
            <Field label={t.elig.phone}><Input value={form.phone} onChange={set("phone")} data-testid="elig-phone" className="h-12" /></Field>
            <Field label={t.elig.airline}><Input value={form.airline} onChange={set("airline")} data-testid="elig-airline" className="h-12" /></Field>
            <Field label={t.elig.flight_number}><Input value={form.flight_number} onChange={set("flight_number")} data-testid="elig-flight-number" className="h-12" /></Field>
            <Field label={t.elig.flight_date}><Input type="date" value={form.flight_date} onChange={set("flight_date")} data-testid="elig-flight-date" className="h-12" /></Field>
            <Field label={t.elig.departure}><Input value={form.departure_airport} onChange={set("departure_airport")} data-testid="elig-departure" className="h-12" /></Field>
            <Field label={t.elig.arrival}><Input value={form.arrival_airport} onChange={set("arrival_airport")} data-testid="elig-arrival" className="h-12" /></Field>
            <Field label={t.elig.issue}>
              <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
                <SelectTrigger className="h-12" data-testid="elig-issue-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="delay">{t.elig.issue_delay}</SelectItem>
                  <SelectItem value="cancellation">{t.elig.issue_cancel}</SelectItem>
                  <SelectItem value="denied_boarding">{t.elig.issue_denied}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t.elig.delay}>
              <Select value={form.delay_length} onValueChange={(v) => setForm({ ...form, delay_length: v })}>
                <SelectTrigger className="h-12" data-testid="elig-delay-length"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="under_3">{t.elig.delay1}</SelectItem>
                  <SelectItem value="3_4">{t.elig.delay2}</SelectItem>
                  <SelectItem value="over_4">{t.elig.delay3}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label={t.elig.details}><Textarea rows={3} value={form.details} onChange={set("details")} data-testid="elig-details" /></Field>

          <div className="pt-2 space-y-3 border-t border-stone-100">
            {consentRows.map((c) => (
              <label key={c.k} className="flex items-start gap-3 cursor-pointer" data-testid={`consent-${c.k}`}>
                <Checkbox checked={consents[c.k]} onCheckedChange={toggleC(c.k)} className="mt-0.5" data-testid={`consent-checkbox-${c.k}`} />
                <span className="text-sm text-stone-600 leading-snug">{c.label}</span>
              </label>
            ))}
            <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-c4">
              <Checkbox checked={consents.c4} onCheckedChange={toggleC("c4")} className="mt-0.5" data-testid="consent-checkbox-c4" />
              <span className="text-sm text-stone-600 leading-snug">
                {t.elig.consent4_pre}
                <Link to="/termeni" target="_blank" className="text-sky-700 underline hover:text-sky-800">{t.elig.terms}</Link>
                {t.elig.and}
                <Link to="/confidentialitate" target="_blank" className="text-sky-700 underline hover:text-sky-800">{t.elig.privacy}</Link>.
              </span>
            </label>
          </div>

          <Button type="submit" disabled={loading} data-testid="elig-submit"
            className="w-full h-14 text-base bg-sky-700 hover:bg-sky-800 text-white font-bold rounded-full">
            {loading ? t.elig.sending : t.elig.submit}
          </Button>

          <p className="text-xs text-stone-500 leading-relaxed" data-testid="elig-disclaimer">{t.elig.important}</p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-stone-700">{label}{required && <span className="text-amber-600"> *</span>}</Label>
      {children}
    </div>
  );
}

function Faq() {
  const { t } = useLang();
  return (
    <section id="faq" className="bg-white py-12 lg:py-16 scroll-mt-16">
      <div className="max-w-3xl mx-auto px-6">
        <h2 className="font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-stone-900 text-center">{t.faq.title}</h2>
        <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
          {t.faq.items.map((it, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-stone-200">
              <AccordionTrigger className="text-left font-head font-semibold text-lg text-stone-900 hover:text-sky-700" data-testid={`faq-q-${i}`}>{it.q}</AccordionTrigger>
              <AccordionContent className="text-stone-600 leading-relaxed text-base">{it.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function Contact() {
  const { t } = useLang();
  const empty = { full_name: "", email: "", phone: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) return toast.error(t.validation);
    setLoading(true);
    try {
      await axios.post(`${API}/submissions/contact`, form);
      toast.success(t.contact.success);
      setForm(empty);
    } catch (err) { toast.error(t.error); } finally { setLoading(false); }
  };

  const contactItems = [
    { icon: PhoneCall, label: t.contact.phone_label, value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}` },
    { icon: MessageCircleHeart, label: t.contact.wa_label, value: CONTACT.whatsapp, href: `https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, "")}` },
    { icon: Mail, label: t.contact.email_label, value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  ];

  return (
    <section id="contact" className="bg-stone-50 py-12 lg:py-16 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-700">{t.nav.contact}</span>
          <h2 className="mt-3 font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-stone-900">{t.contact.title}</h2>
          <p className="mt-3 text-lg text-stone-600">{t.contact.subtitle}</p>
          <div className="mt-8 rounded-3xl overflow-hidden border border-stone-200">
            <img src={SUPPORT} alt="" className="w-full h-52 object-cover" />
          </div>
          <div className="mt-6 space-y-3">
            {contactItems.map((c, i) => (
              <a key={i} href={c.href} target="_blank" rel="noreferrer" data-testid={`contact-info-${i}`}
                className="flex items-center gap-4 bg-white border border-stone-200 rounded-2xl p-4 hover:border-sky-700 hover:-translate-y-0.5 transition-all">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-sky-50 text-sky-700"><c.icon className="h-5 w-5" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-stone-500">{c.label}</div>
                  <div className="text-stone-900 font-medium">{c.value}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="bg-white border border-stone-200 rounded-3xl p-6 sm:p-10 shadow-sm space-y-5 self-start" data-testid="contact-form">
          <Field label={t.contact.full_name} required><Input value={form.full_name} onChange={set("full_name")} data-testid="contact-full-name" className="h-12" /></Field>
          <Field label={t.contact.email} required><Input type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="h-12" /></Field>
          <Field label={t.contact.phone}><Input value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="h-12" /></Field>
          <Field label={t.contact.message} required><Textarea rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" /></Field>
          <Button type="submit" disabled={loading} data-testid="contact-submit"
            className="w-full h-14 text-base bg-amber-500 hover:bg-amber-600 text-stone-900 font-bold rounded-full">
            {loading ? t.contact.sending : t.contact.submit} <Send className="ml-2 h-4 w-4" />
          </Button>
        </form>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-stone-900 text-stone-400 py-14">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row justify-between gap-8">
          <div className="max-w-sm">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center h-9 w-9 rounded-xl bg-sky-700 text-white"><Plane className="h-5 w-5 -rotate-45" /></span>
              <span className="font-head font-extrabold text-xl text-white">Zbor<span className="text-amber-400">Help</span></span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">{t.footer.tagline}</p>
          </div>
          <div className="text-sm space-y-2">
            <a href={`mailto:${CONTACT.email}`} className="block hover:text-white">{CONTACT.email}</a>
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="block hover:text-white">{CONTACT.phone}</a>
            <Link to="/termeni" className="block hover:text-white" data-testid="footer-terms">{t.footer.terms}</Link>
            <Link to="/confidentialitate" className="block hover:text-white" data-testid="footer-privacy">{t.footer.privacy}</Link>
            <Link to="/cookies" className="block hover:text-white" data-testid="footer-cookies">{t.footer.cookies}</Link>
            <Link to="/login" className="block hover:text-white text-stone-500" data-testid="footer-admin-link">{t.nav.admin}</Link>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-white/10 text-xs space-y-2">
          <p>{t.footer.support}</p>
          <p>{t.footer.disclaimer}</p>
          <p>© {new Date().getFullYear()} ZborHelp. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
}

function FloatingWhatsApp() {
  const { t } = useLang();
  const href = `https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(t.wa.msg)}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
      className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-3 pr-4 py-3 shadow-lg shadow-green-900/20 hover:scale-105 hover:bg-[#20bd5a] transition-transform">
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
      <span className="hidden sm:inline font-semibold text-sm">{t.wa.float}</span>
    </a>
  );
}

export default function LandingPage() {
  return (
    <div className="bg-stone-50" data-testid="landing-page">
      <Header />
      <Hero />
      <Trust />
      <How />
      <Why />
      <EligibilityForm />
      <Faq />
      <Contact />
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
